import * as THREE from 'three/webgpu';
import type { RawPointerEventPayload } from '../../viewer/moudles/EventManager';
import type { MeasureController } from '../../measure/controller/MeasureController';
import { isMeasurableObject } from '../../measure/selection/MeasureHitResolver';
import type { MeasurePickScopeResolver } from '../../measure/selection/MeasurePickScopeResolver';
import type { EntitySelectionManager } from '../EntitySelectionManager';
import { AbstractEntitySelectionHandler } from './AbstractEntitySelectionHandler';

/**
 * 测量叠加层实体选择处理器。
 *
 * 职责：
 * 1. 仅在测量会话且拾取启用时接管 click / hover；
 * 2. 按底层模式分派候选集，避免所有场景共用装配体拾取规则；
 * 3. 禁用拖拽与框选，避免污染底层命令交互。
 */
export class MeasureEntitySelectionHandler extends AbstractEntitySelectionHandler {
    /** 测量命令控制器。 */
    private readonly measureController: MeasureController;

    /** 测量拾取候选集解析器。 */
    private readonly pickScopeResolver: MeasurePickScopeResolver;

    /**
     * @param entitySelectionManager 实体选择管理器。
     * @param measureController 测量命令控制器。
     * @param pickScopeResolver 测量拾取候选集解析器。
     */
    public constructor(
        entitySelectionManager: EntitySelectionManager,
        measureController: MeasureController,
        pickScopeResolver: MeasurePickScopeResolver,
    ) {
        super(entitySelectionManager);
        this.measureController = measureController;
        this.pickScopeResolver = pickScopeResolver;
    }

    /**
     * 测量叠加层禁用鼠标按下拖拽链路。
     *
     * @param payload 原始事件。
     */
    public override onMouseDown(payload: RawPointerEventPayload): void {
        void payload;
    }

    /**
     * 测量叠加层禁用鼠标抬起框选结算。
     *
     * @param payload 原始事件。
     */
    public override onMouseUp(payload: RawPointerEventPayload): void {
        void payload;
    }

    /**
     * 处理测量叠加层移入高亮。
     *
     * @param payload 原始事件。
     */
    public override onMouseMove(payload: RawPointerEventPayload): void {
        if (payload.buttons !== 0) {
            this.clearHoverState();
            return;
        }
        this.queueHoverInput(payload.clientX, payload.clientY);
    }

    /** @inheritdoc 使用当前模式的候选规则消费最新悬停坐标。 */
    public override consumeHoverInput(clientX: number, clientY: number): void {
        const baseViewerMode = this.measureController.getBaseViewerMode();
        const hoveredPick = this.pickScopeResolver.pickEntityAt(
            baseViewerMode,
            clientX,
            clientY,
            'hover',
        );
        const measurablePick = hoveredPick && isMeasurableObject(hoveredPick.object)
            ? hoveredPick
            : null;
        this.updateHoverState(
            measurablePick
                ? this.resolveHoverPickWithSelectionExclusion(measurablePick)
                : null,
        );
    }

    /**
     * 处理测量叠加层点击拾取。
     *
     * @param payload 原始事件。
     */
    public override onClick(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }
        if (this.entitySelectionManager.getSuppressNextClick()) {
            this.entitySelectionManager.setSuppressNextClick(false);
            return;
        }

        const baseViewerMode = this.measureController.getBaseViewerMode();
        const rawPicked = this.pickScopeResolver.pickEntityAt(
            baseViewerMode,
            payload.clientX,
            payload.clientY,
            'click',
        );
        const picked = this.pickScopeResolver.resolveClickPick(baseViewerMode, rawPicked);
        if (!picked) {
            this.measureController.clearSelectedEntities();
            return;
        }
        if (!isMeasurableObject(picked.object)) {
            return;
        }

        const accepted = this.measureController.handlePick(picked);
        if (!accepted) {
            this.entitySelectionManager.renderer.drawingNoticeCallback?.(
                '当前实体不支持测量',
                'warning',
            );
        }
    }

    /**
     * 测量叠加层禁用框选。
     *
     * @returns 始终返回 false。
     */
    protected override isBoxSelectionEnabled(): boolean {
        return false;
    }
}
